import { useState } from 'react';
import type { PlacedButton } from '../till/actions';
import { Icon } from './icons';
import { cx, Kbd } from './ui';
import { useT } from '../i18n';

/**
 * The till's function buttons, laid out as the store configured them (Admin -> Buttons).
 *
 * Draws nothing when no button is placed, which is the case on a store with every new feature off,
 * so the till looks exactly as it did before buttons existed. Several pages become tabs; a wide
 * (2x1) button spans two columns; long labels (Tamil runs long) shrink and then ellipsise, with the
 * full label kept in the tooltip and the accessible name.
 */
export function FunctionBar({
  placed,
  keyFor,
  onBlocked,
}: {
  placed: PlacedButton[];
  keyFor: (entry: PlacedButton) => string | null;
  /** A press on a disabled button: say why, rather than doing nothing. */
  onBlocked?: (entry: PlacedButton) => void;
}) {
  const t = useT();
  const pages = [...new Set(placed.map((entry) => entry.button.page))].sort((a, b) => a - b);
  const [chosen, setChosen] = useState(0);
  if (placed.length === 0) return null;
  const page = pages.includes(chosen) ? chosen : (pages[0] ?? 0);
  const shown = placed.filter((entry) => entry.button.page === page);

  return (
    <section aria-label={t('fn.bar')} data-testid="function-bar" className="shrink-0">
      {pages.length > 1 ? (
        <div className="mb-1.5 flex gap-1.5" role="tablist" aria-label={t('fn.pages')}>
          {pages.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={value === page}
              className="chip"
              aria-pressed={value === page}
              onClick={() => setChosen(value)}
            >
              {t('fn.page', { page: value + 1 })}
            </button>
          ))}
        </div>
      ) : null}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 xl:grid-cols-6">
        {shown.map((entry) => {
          const key = keyFor(entry);
          const disabled = entry.action.disabledReason !== null;
          return (
            <button
              key={entry.button.id}
              type="button"
              data-testid={`fn-${entry.button.actionId}`}
              className={cx(
                'btn btn-touch min-w-0 flex-col items-start justify-center gap-0.5 px-2.5 py-2 text-left short:flex-row short:items-center short:py-1',
                entry.button.size === '2x1' && 'col-span-2',
                entry.action.pressed && 'btn-primary',
                entry.button.tone === 'warn' && !entry.action.pressed && 'border-amber-300',
                entry.button.tone === 'danger' && !entry.action.pressed && 'border-red-300',
              )}
              aria-pressed={entry.action.pressed === undefined ? undefined : entry.action.pressed}
              aria-disabled={disabled || undefined}
              title={disabled ? `${entry.label}: ${entry.action.disabledReason}` : key ? `${entry.label} (${key})` : entry.label}
              onClick={() => {
                if (disabled) onBlocked?.(entry);
                else entry.action.run(entry.button.params);
              }}
              style={disabled ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
            >
              <span className="flex w-full min-w-0 items-center gap-1.5">
                <Icon name={entry.action.icon} size={16} />
                <span className="min-w-0 flex-1 truncate text-[clamp(0.72rem,1.4vw,0.9rem)] font-semibold">{entry.label}</span>
              </span>
              {key ? <Kbd className="max-sm:hidden short:hidden">{key}</Kbd> : null}
              {disabled ? <span className="sr-only">{entry.action.disabledReason}</span> : null}
            </button>
          );
        })}
      </div>
    </section>
  );
}
